import React, { useMemo, useEffect } from 'react';
import cn from 'classnames';
import Checkbox from '@dealroadshow/uikit-checkbox';
import FilterGroup from '@framework/ui/Organisms/Filters/components/FilterGroup';
import FilterItem from '@framework/ui/Organisms/Filters/components/FilterItem';
import filtersStyles from '@framework/ui/Organisms/Filters/filters.module.scss';
import { getIndeterminateState } from '@framework/ui/Organisms/Filters/helpers';

interface IProps {
  form: { [key: string]: any },
  formValues: { [key: string]: any },
  fieldComponent: React.ElementType,
  checkboxComponent?: React.ElementType,
  value: string | number,
  groupTitle: string,
  isDisabled?: boolean,
  supportIndeterminate?: boolean,
  nestedIds: Array<number | string>,
  children: { [key: string | number]: any },
  activeChildrenCount?: number,
  checkedChildrenCount?: number,
  filterByName: string,
  filterByChildrenName: string,
  isExpanded?: boolean,
  withCounter?: boolean,
}

function FilterGroupItem({
  form,
  formValues,
  fieldComponent: Field,
  checkboxComponent: CheckboxComponent = Checkbox,
  value,
  groupTitle,
  isDisabled,
  supportIndeterminate = true,
  nestedIds,
  children,
  activeChildrenCount,
  checkedChildrenCount,
  filterByName,
  filterByChildrenName,
  withCounter = false,
  isExpanded = false,
}: IProps) {
  const indeterminateState = useMemo(() => (
      supportIndeterminate
        ? getIndeterminateState(children, formValues[filterByChildrenName])
        : {}
    ),
    [formValues[filterByChildrenName], supportIndeterminate]);

  useEffect(() => {
    if (supportIndeterminate && (indeterminateState?.checked !== formValues[filterByName]?.includes(value))) {
      const filterByNameProp = formValues[filterByName] || [];
      const checkedValues = indeterminateState?.checked
        ? [...filterByNameProp, value]
        : formValues[filterByName]?.filter((checkedValue) => checkedValue !== value);

      form.change(filterByName, checkedValues);
    }
  }, [indeterminateState]);

  const groupCheckbox = useMemo(() => {
    const handleFilterGroupChange = (event) => {
      const { checked } = event.target;
      if (checked) {
        const nestedUnchecked = children
          .filter(({
            value,
            disabled,
          }) => !formValues[filterByChildrenName]?.includes(value) && !disabled)
          .map(({ value }) => value);
        form.change(filterByChildrenName, [...formValues[filterByChildrenName], ...nestedUnchecked]);
      } else {
        const nestedChecked = children
          .filter(({
            value,
            disabled,
          }) => formValues[filterByChildrenName]?.includes(value) && !disabled)
          .map(({ value }) => value);
        form.change(
          filterByChildrenName,
          formValues[filterByChildrenName]?.filter((value) => !nestedChecked?.includes(value)),
        );
      }
    };

    return (
      <Field
        type="checkbox"
        name={ filterByName }
        dataTest={ filterByName }
        value={ value }
        component={ CheckboxComponent }
        label={ groupTitle }
        supportIndeterminate
        disabled={ isDisabled }
        onChange={ handleFilterGroupChange }
        { ...indeterminateState }
      />
    );
  }, [indeterminateState, children, nestedIds]);

  return (
    <FilterGroup
      key={ value }
      groupId={ value }
      isExpanded={ isExpanded }
      showArrow={ !!(children && children.length) }
      itemsCount={ activeChildrenCount }
      selectedItemsCount={ checkedChildrenCount }
      checkboxComponent={ groupCheckbox }
      withCounter={ withCounter }
    >
      { children.map(({
        value,
        label,
        disabled,
      }) => (
        <FilterItem
          key={ `${ value }-${ label }` }
          className={ cn(filtersStyles.filterItem, filtersStyles.filterNestedItem) }
        >
          <Field
            type="checkbox"
            name={ filterByChildrenName }
          >
            { ({ input }) => (
              <CheckboxComponent
                dataTest={ filterByChildrenName }
                { ...input }
                value={ value }
                disabled={ disabled }
                checked={ formValues[filterByChildrenName]?.includes(value) }
                onChange={ (event) => {
                  const { checked } = event.target;
                  if (checked) {
                    form.change(filterByChildrenName, [...formValues[filterByChildrenName], value]);
                  } else {
                    form.change(filterByChildrenName, formValues[filterByChildrenName].filter((id) => id !== value));
                  }
                } }
                label={ label }
              />
            ) }
          </Field>
        </FilterItem>
      )) }
    </FilterGroup>
  );
}

export default FilterGroupItem;
